import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export const CustomSelect = ({ value, onValueChange, children, placeholder = "Select..." }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState('');
    const selectRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        // Find the selected item's label
        const items = React.Children.toArray(children);
        const selectedItem = items.find(child => child.props.value === value);
        setSelectedLabel(selectedItem ? selectedItem.props.children : '');
    }, [value, children]);

    const handleSelect = (itemValue, itemLabel) => {
        onValueChange(itemValue);
        setSelectedLabel(itemLabel);
        setIsOpen(false);
    };

    return (
        <div className="relative w-full" ref={selectRef}>
            {/* Trigger */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex h-11 w-full items-center justify-between rounded-[5px] border-2 border-border bg-white px-3 py-2 text-text-primary transition-all focus:outline-none focus:border-primary hover:border-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
                <span className={`block truncate text-left text-[16px] ${!selectedLabel ? 'text-gray-400' : 'text-text-primary'}`}>
                    {selectedLabel || placeholder}
                </span>
                <ChevronDown
                    className={`h-4 w-4 text-muted-foreground ml-2 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-md max-h-96 overflow-y-auto">
                    <div className="p-1">
                        {React.Children.map(children, (child) => {
                            if (child.type === CustomSelectItem) {
                                return React.cloneElement(child, {
                                    isSelected: child.props.value === value,
                                    onSelect: () => handleSelect(child.props.value, child.props.children)
                                });
                            }
                            return child;
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export const CustomSelectItem = ({ value, children, isSelected, onSelect }) => {
    return (
        <button
            type="button"
            onClick={onSelect}
            className={`relative flex w-full cursor-pointer select-none items-center justify-between rounded-[5px] py-2 pl-3 pr-3 text-sm text-text-primary outline-none hover:bg-background-light focus:bg-background-light transition-colors ${isSelected ? 'bg-background-light' : ''
                }`}
        >
            <span className="block truncate text-left">{children}</span>
            {isSelected && (
                <svg
                    className="h-4 w-4 text-text-primary ml-2 flex-shrink-0"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path d="M5 13l4 4L19 7" />
                </svg>
            )}
        </button>
    );
};